import { Clock, Smile } from "lucide-react"
import { UseFormRegister, FieldErrors, UseFormSetValue } from 'react-hook-form'
import { Input, Label } from "@city-sage/aurum-avis-labs-components"
import { StopFormValues } from "../types/createStopTypes"

interface TimingSectionProps {
  showTimingFields: boolean
  onToggleTimingFields: (show: boolean) => void
  watchedDuration: number | '' | undefined
  register: UseFormRegister<StopFormValues>
  errors: FieldErrors<StopFormValues>
  setValue: UseFormSetValue<StopFormValues>
}

const durationOptions = [
  { value: 30, label: '30 min' },
  { value: 60, label: '1 hour' },
  { value: 120, label: '2 hours' },
  { value: 240, label: '4 hours' },
]

export function TimingSection({
  showTimingFields,
  onToggleTimingFields,
  watchedDuration,
  register,
  errors,
  setValue
}: TimingSectionProps) {
  const handleDurationSelect = (minutes: number) => {
    setValue('durationMinutes', minutes)
  }

  return (
    <div className="bg-white mt-2 p-4">
      <div className="space-y-4">
        <div>
          <h2 className="font-semibold mb-1">Duration</h2>
          <p className="text-sm text-gray-600">How long do you plan to stay?</p>
        </div>
        
        {/* Timing Toggle */}
        <div className="p-0.5 bg-gray-100 rounded-xl">
          <div className="grid grid-cols-2 gap-0.5">
            <button
              type="button"
              onClick={() => onToggleTimingFields(false)}
              className={`
                flex items-center justify-center gap-1.5 h-10 px-3 text-xs font-medium rounded-lg transition-all duration-200
                ${!showTimingFields 
                  ? 'bg-white shadow-sm text-gray-900' 
                  : 'text-gray-600 hover:text-gray-900'
                }
              `}
            >
              <Smile className="h-3 w-3" />
              Keep it flexible
            </button>
            
            <button
              type="button"
              onClick={() => onToggleTimingFields(true)}
              className={`
                flex items-center justify-center gap-1.5 h-10 px-3 text-xs font-medium rounded-lg transition-all duration-200
                ${showTimingFields 
                  ? 'bg-white shadow-sm text-gray-900' 
                  : 'text-gray-600 hover:text-gray-900'
                }
              `}
            >
              <Clock className="h-3 w-3" />
              Set timing
            </button>
          </div>
        </div>

        {/* Timing Fields */}
        {showTimingFields && (
          <div className="space-y-4 p-4 rounded-lg border bg-gray-50">
            {/* Quick Duration Selection */}
            <div className="space-y-2">
              <Label className="text-xs text-gray-600 font-medium">
                Quick Duration
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {durationOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleDurationSelect(option.value)}
                    className={`
                      p-2 rounded-lg text-xs font-medium transition-all duration-200
                      ${watchedDuration === option.value
                        ? 'bg-primary text-white shadow-sm'
                        : 'bg-white border hover:border-gray-300'
                      }
                    `}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Time Input Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="startTime" className="text-xs text-gray-600 font-medium">
                  Start Time
                </Label>
                <Input
                  id="startTime"
                  type="time"
                  className="h-11 text-sm"
                  {...register('startTime')}
                />
                {errors.startTime && (
                  <p className="text-xs text-red-600">{String(errors.startTime?.message || '')}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="durationMinutes" className="text-xs text-gray-600 font-medium">
                  Custom Duration (min)
                </Label>
                <Input
                  id="durationMinutes"
                  type="number"
                  placeholder="e.g. 90"
                  min="1"
                  className="h-11 text-sm"
                  {...register('durationMinutes', { 
                    valueAsNumber: true,
                    setValueAs: (value) => value === '' ? '' : parseInt(value)
                  })}
                />
                {errors.durationMinutes && (
                  <p className="text-xs text-red-600">{String(errors.durationMinutes?.message || '')}</p>
                )}
              </div>
            </div>
            {(Number.isInteger(watchedDuration)) && (
              <div className="text-center">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary">
                  <Clock className="h-3 w-3" />
                  <span className="text-xs font-medium">
                    {Math.floor((watchedDuration as number) / 60)}h {(watchedDuration as number) % 60}m planned
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
} 